import { useMemo } from "react";

import { Break } from "@/client";
import { ErrorElement } from "@/components/error-element";
import { LoadingScreen } from "@/components/loading-screen";
import { SearchBar } from "@/components/search-bar";
import { useTableControl } from "@/components/table-control";
import { useGetBreaks } from "@/features/abstracts-sections/api/get-breaks";
import { DataTable } from "@/features/abstracts-sections/components/data-table";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import { pageTitle } from "@/utils/page-title";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

export function BreakListPage() {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<Break>();
  const { pagination, setPagination, search, setSearch } = useTableControl();
  const { congress } = useCongress();
  const {
    data: breaksData,
    isLoading,
    isFetching,
    isPlaceholderData,
    isSuccess,
  } = useGetBreaks({
    congressId: congress.id,
    pageSize: pagination.pageSize,
    pageIndex: pagination.pageIndex,
    search: search ?? undefined,
  });
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => {
          const name = info.getValue();
          if (!name) return "-";
          return name;
        },
        header: () => <span>{t("table.name")}</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("fromTime", {
        id: "fromTime",
        cell: (info) => {
          const startTime = info.getValue();
          if (!startTime) return "-";
          return startTime;
        },
        header: () => <span>{t("table.startTime")}</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("toTime", {
        id: "toTime",
        cell: (info) => {
          const endTime = info.getValue();
          if (!endTime) return "-";
          return endTime;
        },
        header: () => <span>{t("table.endTime")}</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("fromDate", {
        id: "fromDate",
        cell: (info) => {
          const from = info.getValue();
          if (!from) {
            return "-";
          }

          return from;
        },
        header: () => <span>{t("table.from")}</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("toDate", {
        id: "toDate",
        cell: (info) => {
          const to = info.getValue();
          if (!to) {
            return "-";
          }

          return to;
        },
        header: () => <span>{t("table.to")}</span>,
        enableSorting: false,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnHelper, t],
  );

  if (isLoading) return <LoadingScreen />;

  if (!isSuccess) {
    return <ErrorElement />;
  }

  return (
    <div>
      <title>{pageTitle(t("meta.breaks"))}</title>
      <SearchBar search={search} setSearch={setSearch} className="mb-4" />
      <DataTable
        columns={columns}
        data={breaksData.items}
        pagination={pagination}
        pageCount={Math.ceil(breaksData.count / pagination.pageSize)}
        onPaginationChange={setPagination}
        isLoading={isFetching && isPlaceholderData}
      />
    </div>
  );
}
